import { useState, useCallback, useRef } from 'react'
import { fileUploadService, FileUploadOptions, UploadedFile } from '../services/fileUploadService'

export interface FileUploadState {
  isUploading: boolean
  error: string | null
  uploadedFiles: UploadedFile[]
}

export const useFileUpload = () => {
  const [uploadState, setUploadState] = useState<FileUploadState>({
    isUploading: false,
    error: null,
    uploadedFiles: []
  })

  const fileInputRef = useRef<HTMLInputElement>(null)

  // Clear error
  const clearError = useCallback(() => {
    setUploadState(prev => ({ ...prev, error: null }))
  }, [])

  // Validate and process uploaded files
  const processFiles = useCallback(async (files: FileList, options: FileUploadOptions = {}): Promise<UploadedFile[]> => {
    const fileArray = Array.from(files)
    const processedFiles: UploadedFile[] = []

    for (const file of fileArray) {
      // Validate file
      const validation = fileUploadService.validateFile(file, options)
      if (!validation.valid) {
        setUploadState(prev => ({
          ...prev,
          error: validation.error || 'Invalid file'
        }))
        continue
      }

      try {
        // Get duration (this might fail for some files, but we'll continue processing)
        let duration = 0
        try {
          duration = await fileUploadService.getAudioDuration(file)
        } catch (durationError) {
          console.warn(`Could not determine duration for ${file.name}:`, durationError)
          // Continue processing even if duration detection fails
        }

        // Create preview URL
        const preview = fileUploadService.createPreviewURL(file)

        const uploadedFile: UploadedFile = {
          file,
          preview,
          duration,
          size: file.size
        }

        processedFiles.push(uploadedFile)
      } catch (error) {
        console.error('Failed to process file:', file.name, error)

        // Provide more specific error messages
        let errorMessage = `Failed to process file: ${file.name}`
        if (error instanceof Error) {
          if (error.message.includes('timed out')) {
            errorMessage = `File ${file.name} took too long to process. Please try a smaller file.`
          } else if (error.message.includes('format not supported')) {
            errorMessage = `File ${file.name} format is not supported. Please try a different audio file.`
          } else if (error.message.includes('corrupted')) {
            errorMessage = `File ${file.name} appears to be corrupted. Please try a different file.`
          }
        }

        setUploadState(prev => ({
          ...prev,
          error: errorMessage
        }))
      }
    }

    return processedFiles
  }, [])

  // Handle file selection
  const handleFileSelect = useCallback(async (files: FileList, options: FileUploadOptions = {}) => {
    setUploadState(prev => ({
      ...prev,
      isUploading: true,
      error: null
    }))

    try {
      const processedFiles = await processFiles(files, options)
      setUploadState(prev => ({
        ...prev,
        uploadedFiles: processedFiles,
        isUploading: false
      }))
    } catch (error) {
      setUploadState(prev => ({
        ...prev,
        isUploading: false,
        error: error instanceof Error ? error.message : 'Upload failed'
      }))
    }
  }, [processFiles])

  // Open file picker
  const openFilePicker = useCallback(() => {
    if (fileInputRef.current) {
      // Clear previous selection
      fileInputRef.current.value = ''
      fileInputRef.current.click()
    }
  }, [])

  // Handle file input change
  const handleFileInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>, options: FileUploadOptions = {}) => {
    const files = event.target.files
    if (files && files.length > 0) {
      handleFileSelect(files, options)
    }
  }, [handleFileSelect])

  // Clear uploaded files
  const clearUploadedFiles = useCallback(() => {
    // Revoke preview URLs to free memory
    uploadState.uploadedFiles.forEach(file => {
      fileUploadService.revokePreviewURL(file.preview)
    })

    setUploadState(prev => ({
      ...prev,
      uploadedFiles: [],
      error: null
    }))
  }, [uploadState.uploadedFiles])

  // Remove specific uploaded file
  const removeUploadedFile = useCallback((index: number) => {
    const fileToRemove = uploadState.uploadedFiles[index]
    if (fileToRemove) {
      fileUploadService.revokePreviewURL(fileToRemove.preview)

      setUploadState(prev => ({
        ...prev,
        uploadedFiles: prev.uploadedFiles.filter((_, i) => i !== index)
      }))
    }
  }, [uploadState.uploadedFiles])

  // Format file size
  const formatFileSize = useCallback((bytes: number): string => {
    return fileUploadService.formatFileSize(bytes)
  }, [])

  // Format duration
  const formatDuration = useCallback((seconds: number): string => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = Math.floor(seconds % 60)
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
  }, [])

  return {
    uploadState,
    fileInputRef,
    handleFileSelect,
    handleFileInputChange,
    openFilePicker,
    clearError,
    clearUploadedFiles,
    removeUploadedFile,
    formatFileSize,
    formatDuration
  }
}
